import React, { useEffect, useRef, useState } from "react";
import { fetchMarketHistory } from "../lib/api";

/**
 * Lightweight SVG sparkline with lazy IntersectionObserver-based loading.
 * Only fetches price history when the card scrolls into view.
 */
export default function Sparkline({ marketId, height = 28, color = "#00d8ff", interval = "1w" }) {
  const wrapRef = useRef(null);
  const [points, setPoints] = useState(null);
  const [seen, setSeen] = useState(false);

  useEffect(() => {
    if (!wrapRef.current) return;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setSeen(true);
            obs.disconnect();
            break;
          }
        }
      },
      { threshold: 0.1 }
    );
    obs.observe(wrapRef.current);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!seen) return;
    let cancelled = false;
    fetchMarketHistory(marketId, interval, 60)
      .then((d) => {
        if (cancelled) return;
        const h = (d.history || []).map((p) => p.p).filter((v) => typeof v === "number");
        setPoints(h);
      })
      .catch(() => setPoints([]));
    return () => { cancelled = true; };
  }, [seen, marketId, interval]);

  if (!points) {
    return (
      <div
        ref={wrapRef}
        style={{ height }}
        className="w-full bg-white/[0.02] animate-pulse-soft"
        data-testid={`sparkline-loading-${marketId}`}
      />
    );
  }

  if (points.length < 2) {
    return <div ref={wrapRef} style={{ height }} className="w-full" />;
  }

  const w = 100; // viewBox width (responsive via preserveAspectRatio=none)
  const h = height;
  const min = Math.min(...points);
  const max = Math.max(...points);
  const range = max - min || 1;
  const last = points[points.length - 1];
  const first = points[0];
  const up = last >= first;
  const stroke = up ? "#10b981" : "#ef4444";

  const stepX = w / (points.length - 1);
  const yScale = (v) => h - ((v - min) / range) * (h - 2) - 1;

  let d = `M 0 ${yScale(points[0]).toFixed(2)}`;
  for (let i = 1; i < points.length; i++) {
    d += ` L ${(i * stepX).toFixed(2)} ${yScale(points[i]).toFixed(2)}`;
  }
  const fillPath = d + ` L ${w} ${h} L 0 ${h} Z`;

  return (
    <div ref={wrapRef} className="w-full" data-testid={`sparkline-${marketId}`}>
      <svg
        width="100%"
        height={h}
        viewBox={`0 0 ${w} ${h}`}
        preserveAspectRatio="none"
        className="block"
      >
        <defs>
          <linearGradient id={`spark-grad-${marketId}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={stroke} stopOpacity="0.22" />
            <stop offset="100%" stopColor={stroke} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={fillPath} fill={`url(#spark-grad-${marketId})`} />
        <path d={d} fill="none" stroke={stroke} strokeWidth="1.25" />
      </svg>
    </div>
  );
}
